import React from 'react';
// Direct file import — MedicationHistoryList consumes this and re-exports
// via clinical_reference, so the barrel route causes a circular load (see
// MedicationHistoryList's import comment).
import type { ItemListItem } from '@features/server-ui/types';
import { Chip } from '@jotpsych/ui';
import { Box, Typography } from '@mui/material';

const formatShortDate = (iso: string): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
    date,
  );
};

// The resolver packs sig and supply (qty / day-supply / refills / payer) into
// `detail`, separated by newlines, in that order. Splitting here keeps the
// resolver shape narrow and matches how `HistoryItemRow` consumers expect
// `detail` to be free-form text.
const splitDetail = (detail: string | null | undefined): { sig?: string; supply?: string } => {
  if (!detail) return {};
  const [sig, supply] = detail.split('\n', 2);
  return { sig: sig || undefined, supply: supply || undefined };
};

type MedicationHistoryRowProps = {
  item: ItemListItem;
};

export const MedicationHistoryRow: React.FC<MedicationHistoryRowProps> = ({ item }) => {
  const { sig, supply } = splitDetail(item.detail);
  const dateText = item.date ? formatShortDate(item.date) : '';
  const sourceLabel = item.type ?? null;
  const scheduleBadge = item.status ?? null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        py: 1.5,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {item.label}
          </Typography>
          {scheduleBadge && (
            <Chip
              label={scheduleBadge}
              size="xSmall"
              variant="soft"
              color="warning"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        {dateText && (
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {dateText}
          </Typography>
        )}
      </Box>

      {sig && (
        <Typography variant="body2" color="text.secondary">
          {sig}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {supply && (
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
            {supply}
          </Typography>
        )}
        {sourceLabel && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ flexShrink: 0, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {sourceLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
